import { useNavigate } from "@tanstack/react-router";
import { useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { usePostHog } from "posthog-js/react"
import { Body_AccessToken as AccessToken } from "../types/Auth"
import { userLogin, me } from "../api/authApi"
import { ApiError } from "../types/Api"

const isLoggedIn = () => {
  return localStorage.getItem("access_token") !== null
}

const useAuth = () => {
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const posthog = usePostHog()

  const { data: user, isLoading, refetch } = useQuery({
    queryKey: ["currentUser"],
    queryFn: me,
    enabled: isLoggedIn(),
  })

  const login = async (data: AccessToken) => {
    const response = await userLogin(data);
    localStorage.setItem("access_token", response.access_token)
    // Identify by the stable user id (not the email) so anonymous frontend
    // activity merges into the same PostHog person as backend events, which
    // key on user.id. The `user_logged_in` event itself is emitted by the backend.
    const currentUser = await me()
    posthog.identify(currentUser.id, { email: currentUser.email })
  }

  const loginMutation = useMutation({
    mutationFn: login,
    onSuccess: () => {
      navigate({ to: "/projects" })
    },
    onError: (err) => {
      console.error(err)
      if (err instanceof ApiError) {
        if (err.status === 429) {
          setError("Too many login attempts. Please try again in 15 minutes.");
          return;
        }
        const errorBody = err.body as { detail?: string } | string;
        const errorDetail = typeof errorBody === 'object' && errorBody?.detail
          ? errorBody.detail
          : typeof errorBody === 'string'
          ? errorBody
          : "Login failed";

        // Set specific error messages based on the error detail
        if (errorDetail.toLowerCase().includes("incorrect") || errorDetail.toLowerCase().includes("password")) {
          setError("Invalid email or password. Please try again.");
        } else if (errorDetail.toLowerCase().includes("inactive")) {
          setError("Your account is inactive. Please contact support.");
        } else {
          setError(errorDetail);
        }
      } else {
        setError("An unexpected error occurred. Please try again.");
      }
    },
  })

  const logout = () => {
    posthog.capture('user_logged_out')
    posthog.reset()
    localStorage.removeItem("access_token")
    queryClient.clear()
    navigate({ to: "/login" })
  }

  const refreshUser = async () => {
    await refetch()
  }

  return {
    loginMutation,
    logout,
    user,
    error,
    resetError: () => setError(null),
    isLoading,
    refreshUser,
  }
}

export { isLoggedIn }
export default useAuth
